import { SxProps, TextFieldProps } from '@mui/material';
import {
  AutocompleteItem,
  AutocompleteSelect,
} from '@prismamedia/one-components';
import { FC, useCallback, useState } from 'react';
import { searchImageSources } from '../../../../apollo/sharedQueries/getSourcesCategories.web.graphql';
import { getCreditInList } from './utils';

interface ImageSourceSelectProps {
  label?: string;
  value?: AutocompleteItem;
  onChange?: (value?: AutocompleteItem) => void;
  className?: string;
  TextFieldProps?: TextFieldProps;
  sx?: SxProps;
  valueToSelectFrom?: string;
}

export const ImageSourceSelect: FC<ImageSourceSelectProps> = ({
  value,
  onChange,
  className,
  label = "Source de l'image",
  TextFieldProps: textFieldProps,
  sx,
  valueToSelectFrom,
}) => {
  // When removing the value, the field will not be automatically set to the valueToSelectFrom
  const [hasBeenSet] = useState(!!value);

  const fetchList = useCallback((search: string) => {
    return searchImageSources(search).then((imageSources) => {
      const grouped = imageSources?.reduce((acc, source) => {
        const { id, title, category } = source;
        if (!category) return acc;

        return {
          ...acc,
          [category.id]: {
            ...(acc[category.id] || {
              id: category.id,
              title: category.title,
              sources: [],
            }),
            sources: [...(acc[category.id]?.sources || []), { id, title }],
          },
        };
      }, {} as Record<string, { id: string; title: string; sources: { id: string; title: string }[] }>);

      return Object.values(grouped).reduce(
        (prev, { id, title, sources }) => [
          ...prev,
          { id, label: title, disabled: true },
          ...sources.map((child) => ({
            id: child.id,
            label: child.title,
            level: 1,
          })),
        ],
        [] as AutocompleteItem[],
      );
    });
  }, []);

  // Select source based on valueToSelectFrom
  if (!value && valueToSelectFrom && !hasBeenSet) {
    fetchList('')
      .then((imageSourceCategories) => {
        const sourceFound = getCreditInList(
          imageSourceCategories,
          valueToSelectFrom,
        );
        if (sourceFound) {
          onChange?.(sourceFound);
        }
      })
      .catch(() => {});
  }

  return (
    <AutocompleteSelect
      sx={sx}
      label={label}
      value={value}
      className={className}
      fetchList={fetchList}
      onChange={onChange}
      TextFieldProps={textFieldProps}
    />
  );
};
